/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { shortLanguage } from '../utils/orgLabels';
import { uiLocale } from '../utils/locale';

const BellIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
);

export const NotificationBell: React.FC = () => {
    const { t, i18n } = useTranslation();

    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useFocusTrap(dropdownRef, isOpen);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleNotificationClick = async (notif: { id: string; link?: string; read: boolean }) => {
        if (!notif.read) {
            await markAsRead(notif.id);
        }
        if (notif.link) {
            navigate(notif.link);
            setIsOpen(false);
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label={t('notificationbell.benachrichtigungen')}
            >
                <BellIcon className="w-6 h-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white"></span>
                    </span>
                )}
            </button>

            {isOpen && (
                <div 
                    ref={dropdownRef} 
                    role="dialog"
                    aria-label={t('notificationbell.benachrichtigungen')}
                    className="absolute left-0 bottom-full mb-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-[100] overflow-hidden flex flex-col max-h-96 transform origin-bottom-left transition-all"
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.stopPropagation();
                            setIsOpen(false);
                        }
                    }}
                >
                    <div className="flex justify-between items-center p-3 border-b border-gray-100 bg-gray-50/50">
                        <h2 className="font-bold text-gray-900 text-sm">{t('notificationbell.benachrichtigungen')}</h2>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                            >
                                {t('notificationbell.alleAlsGelesenMarkieren')}
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('notificationbell.keineBenachrichtigungenVorhanden')}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-50">
                                {notifications.map((notif) => (
                                    <button
                                        key={notif.id}
                                        onClick={() => handleNotificationClick(notif)}
                                        className={cn(
                                            "w-full text-left p-3 hover:bg-gray-50 transition-colors flex flex-col gap-1.5 focus:outline-none focus-visible:bg-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500",
                                            !notif.read ? "bg-blue-50/30" : ""
                                        )}
                                    >
                                        <div className="flex justify-between items-start gap-2">
                                            <span className={cn(
                                                "text-sm font-medium",
                                                !notif.read ? "text-gray-900" : "text-gray-600"
                                            )}>
                                                {notif.i18n?.[shortLanguage(i18n.language)]?.title
                                                    ?? (notif.type === 'reminder'
                                                        ? t('notifications.reminder')
                                                        : notif.type === 'mention'
                                                        ? t('notifications.mention')
                                                        : notif.type === 'reorganization'
                                                        ? t('notifications.reorganization', 'Einheit verschoben')
                                                        : notif.title)}
                                            </span>
                                            {!notif.read && (
                                                <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed">
                                            {notif.i18n?.[shortLanguage(i18n.language)]?.message ?? notif.message}
                                        </p>
                                        <span className="text-[10px] text-gray-600 font-medium">
                                            {new Date(notif.createdAt).toLocaleString(uiLocale(i18n.language))}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
